import { TRACKER_COLORS as C, MONO, formatShortDate } from "../../constants/trackerUi";
import { useMyStages } from "../../api/trackerApi";

/**
 * What's sitting with you right now, across every project.
 *
 * Without this you'd have to walk each project to find your own work — which
 * is exactly the hunt Tracker is supposed to remove. Blocked first, then
 * waiting-to-be-accepted, then in progress.
 */
export default function MyStagesStrip({ onJump }) {
  const { data: stages, isLoading } = useMyStages();

  if (isLoading || !stages?.length) return null;

  const tone = (s) =>
    s.status === "blocked"
      ? { bg: C.warnSoft, color: C.warn, label: "Blocked" }
      : s.status === "pending_accept"
      ? // Reads as state, not a button — the card jumps you to the requirement,
        // where the Accept button actually lives.
        { bg: C.accentSoft, color: C.accentInk, label: "To accept" }
      : { bg: C.doneSoft, color: C.done, label: "In progress" };

  return (
    <div
      style={{
        background: C.surface,
        border: `1px solid ${C.line}`,
        borderRadius: 13,
        padding: "13px 15px",
        marginBottom: 18,
      }}
    >
      <div
        style={{
          fontFamily: MONO,
          fontSize: "0.62rem",
          letterSpacing: "0.07em",
          textTransform: "uppercase",
          color: C.inkFaint,
          marginBottom: 10,
        }}
      >
        With you — {stages.length}
      </div>
      <div style={{ display: "flex", gap: 9, overflowX: "auto", paddingBottom: 4 }}>
        {stages.map((s) => {
          const t = tone(s);
          const over = s.daysElapsed != null && s.etaDays != null && s.daysElapsed > s.etaDays;
          return (
            <button
              key={`${s.requirementId}-${s.stageCode}`}
              onClick={() => onJump?.(s)}
              style={{
                flex: "0 0 auto",
                minWidth: 215,
                maxWidth: 215,
                textAlign: "left",
                background: C.surface,
                border: `1px solid ${s.status === "blocked" ? C.warn : C.line}`,
                borderRadius: 11,
                padding: "10px 12px",
                cursor: "pointer",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 6, marginBottom: 6 }}>
                <span
                  style={{
                    fontSize: "0.62rem",
                    fontWeight: 800,
                    padding: "2px 8px",
                    borderRadius: 100,
                    background: t.bg,
                    color: t.color,
                    whiteSpace: "nowrap",
                  }}
                >
                  {t.label}
                </span>
                <span style={{ fontFamily: MONO, fontSize: "0.63rem", color: over ? C.danger : C.inkFaint, fontWeight: 700 }}>
                  {s.daysElapsed != null ? `${s.daysElapsed}/${s.etaDays ?? "–"}d` : s.etaDate ? formatShortDate(s.etaDate) : ""}
                </span>
              </div>
              <div
                style={{
                  fontSize: "0.8rem",
                  fontWeight: 700,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  marginBottom: 2,
                }}
                title={s.title}
              >
                {s.title}
              </div>
              <div style={{ fontSize: "0.68rem", color: C.inkFaint, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {s.stageLabel} · {s.projectName || s.projectId}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
